import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Url } from '@wix/da-url';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import {
  BiData,
  GiveBadgeAwardBiEvent,
  MoreLikeThisClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiveBadge from '@wix/da-ds/pkg/Icons/24x24/GiveBadge';
import MltSearch24 from '@wix/da-ds/pkg/Icons/24x24/MltSearch';
import Boost24 from '@wix/da-ds/pkg/Icons/24x24/Boost';
import BoostFilled24 from '@wix/da-ds/pkg/Icons/24x24/BoostFilled';
import BoostRootModal from '../../../../../ActionBar/buttons/BoostButton/modals/BoostRootModal';
import { useIsCurrentUserDeviationAuthor } from '../../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useSubscriptionTier } from '../../../../../utils/hooks/useSubscriptionTier';
import { beginDeviationAwardBadgeFlow } from '../../../../../coreBadge/redux/actionCreators';
import { AwardBadgeSource } from '../../../../../coreBadge/types';
import { useOverlayContext } from '../../../../hooks/useOverlayContext';
import { useDeviationBlockType } from '../../../hooks/useDeviationBlockType';
import FaveControl from '../../../legos/FaveControl';
import CommentControl from '../../../legos/CommentControl';
import { getDeviationResourceType, getIsPost } from '../../../../../DeviationViews/_helpers';
import { DeviationResourceType } from '../../../../../types/deviation';
import { useRestrictToLoggedIn } from '../../../../../utils/hooks/useRestrictToLoggedIn';
import { useDeviationAttributes } from '../../../../hooks/useDeviationAttributes';

import s from './BottomInfoBar.scss';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const BottomInfoBar: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const { isCoreBadgeAwardable, isBoosted } = useDeviationAttributes(deviation, isAuthor);
  const { showCommentControl, showFaveControl } = useOverlayContext(deviation);
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const resourceType = getDeviationResourceType(deviation);
  const { needsSubscription, isSubscribedButBlocked } = useSubscriptionTier(deviation);
  const isBlockedBySubscription = needsSubscription || isSubscribedButBlocked;
  const [showBoostModal, setShowBoostModal] = useState(false);

  const dispatch = useDispatch();
  const handleAwardBadgeClick = useCallback(() => {
    dispatch(beginDeviationAwardBadgeFlow(deviation, AwardBadgeSource.THUMB_INFO));
  }, [deviation, dispatch]);

  const blockType = useDeviationBlockType(deviation);
  const isMlt =
    resourceType === DeviationResourceType.ART &&
    !getIsPost(deviation) &&
    (!blockType || blockType === 'matureFilter');
  const onMlt = useCallback(() => {
    window.location.assign(Url.moreLikeThisLink(deviation.deviationId));
  }, [deviation]);

  const isBoostIconShowable = isAuthor;

  return (
    <div className={classnames(s['root'], s['with-badges'], className)}>
      <div className={s['controls']}>
        {showFaveControl && (
          <FaveControl
            iconShapeSize={IconSize.MEDIUM}
            deviation={deviation}
            className={s['stat']}
          />
        )}
        {showCommentControl && (
          <CommentControl
            iconShapeSize={IconSize.MEDIUM}
            deviation={deviation}
            className={s['stat']}
          />
        )}
      </div>

      {(isCoreBadgeAwardable || isMlt || isBoostIconShowable) && (
        <div className={s['actions']}>
          {isCoreBadgeAwardable && !isBlockedBySubscription && (
            <BiButton
              className={s['award-button']}
              onClick={restrictToLoggedIn(ReferralType.AWARD_BADGE, handleAwardBadgeClick)}
              biData={BiData<GiveBadgeAwardBiEvent>({
                evid: 320,
                sectionname: BiContextProvidedValue,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
              })}
            >
              <GiveBadge />
            </BiButton>
          )}

          {isMlt && (
            <BiButton
              className={s['mlt-button']}
              onClick={onMlt}
              biData={BiData<MoreLikeThisClickBiEvent>({
                evid: 126,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
                creatorid: deviation.author?.useridUuid ?? '',
                sectionname: BiContextProvidedValue,
              })}
              aria-label={t('common.moreLikeThis')}
            >
              <MltSearch24 className={s['mlt-icon']} />
            </BiButton>
          )}

          {isBoostIconShowable && (
            <BiButton
              className={s['boost-button']}
              onClick={() => setShowBoostModal(true)}
              biData={BiData<GiveBadgeAwardBiEvent>({
                evid: 320,
                sectionname: BiContextProvidedValue,
                itemid: deviation.deviationId,
                typeid: getDeviationResourceType(deviation),
                badge_type: 'boost',
                badge_typeid: 176,
              })}
              aria-label={t(isBoosted ? 'actionbar.currentlyBoosted' : 'actionbar.boost')}
            >
              {isBoosted ? (
                <BoostFilled24 className={s['boost-icon']} />
              ) : (
                <Boost24 className={s['boost-icon']} />
              )}
            </BiButton>
          )}
        </div>
      )}

      {showBoostModal && (
        <BoostRootModal deviation={deviation} onRequestClose={() => setShowBoostModal(false)} />
      )}
    </div>
  );
};
BottomInfoBar.displayName = 'BottomInfoBar';

export default BottomInfoBar;
